import { useState } from 'react';

interface GallerySectionProps {
  images: string[];
  title: string;
}

export default function GallerySection({ images, title }: GallerySectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (images.length === 0) return null;

  const gridImages = images.slice(0, 5);

  return (
    <>
      {/* Desktop: 1 large + 2x2 grid */}
      <div className="hidden lg:grid grid-cols-[2fr_1fr_1fr] grid-rows-[1fr_1fr] gap-2 h-[390px]">
        {/* Main large image */}
        <button
          type="button"
          className="relative row-span-2 overflow-hidden rounded-[14px] bg-secondary-100 cursor-pointer group"
          onClick={() => { setActiveIndex(0); setLightboxOpen(true); }}
          aria-label={`Open ${title}`}
        >
          <img
            src={gridImages[0]}
            alt={`${title} - 1`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <span className="absolute bottom-3.5 right-3.5 flex items-center gap-1.5 rounded-[9px] bg-white/94 px-2.5 py-1.5 text-[12px] font-semibold text-secondary-700">
            1 / {gridImages.length}
          </span>
        </button>
        {/* 4 smaller images */}
        {gridImages.slice(1, 5).map((img, idx) => (
          <button
            key={idx + 1}
            type="button"
            className="relative overflow-hidden rounded-[14px] bg-secondary-100 cursor-pointer group"
            onClick={() => { setActiveIndex(idx + 1); setLightboxOpen(true); }}
            aria-label={`Open ${title}`}
          >
            <img
              src={img}
              alt={`${title} - ${idx + 2}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Mobile: 2 cols x 2 rows */}
      <div className="lg:hidden grid grid-cols-2 grid-rows-[180px_110px] gap-2">
        <button
          type="button"
          className="relative row-span-2 overflow-hidden rounded-[14px] bg-secondary-100 cursor-pointer"
          onClick={() => { setActiveIndex(0); setLightboxOpen(true); }}
          aria-label={`Open ${title}`}
        >
          <img src={gridImages[0]} alt={`${title} - 1`} className="w-full h-full object-cover" loading="lazy" />
          <span className="absolute bottom-3.5 right-3.5 flex items-center gap-1.5 rounded-[9px] bg-white/94 px-2.5 py-1.5 text-[12px] font-semibold text-secondary-700">
            1 / {gridImages.length}
          </span>
        </button>
        {gridImages.slice(1, 5).map((img, idx) => (
          <button
            key={idx + 1}
            type="button"
            className="relative overflow-hidden rounded-[14px] bg-secondary-100 cursor-pointer"
            onClick={() => { setActiveIndex(idx + 1); setLightboxOpen(true); }}
            aria-label={`Open ${title}`}
          >
            <img src={img} alt={`${title} - ${idx + 2}`} className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={gridImages[activeIndex]}
              alt={`${title} - ${activeIndex + 1}`}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {gridImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    i === activeIndex ? 'bg-white scale-125' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-3 right-3 w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
            {activeIndex > 0 && (
              <button
                onClick={() => setActiveIndex((p) => p - 1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
            )}
            {activeIndex < gridImages.length - 1 && (
              <button
                onClick={() => setActiveIndex((p) => p + 1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}